// The memory screens' query options (M7-04): memory review, accepted/rejected promotions, the
// memory proxy (the side panel) and Setup › Recall. Every key but the search starts
// `["company", co, "memory"]`: a promotion decision invalidates that prefix (and Work's pages,
// which gained one). The search key stands apart (RECALL-SEARCH, below).
import type { OperationOutput } from "@companyos/sdk";
import { infiniteQueryOptions, type QueryClient, queryOptions } from "@tanstack/react-query";
import type { Api } from "../../api/client";
import { workKeys } from "../work/work-queries";

export type MemoryReview = OperationOutput<"memory.review">;
export type PromotionItem = MemoryReview["proposals"][number];
export type ContradictionItem = MemoryReview["contradictions"][number];
export type ConflictItem = MemoryReview["conflicts"][number];
export type DecisionItem = MemoryReview["decisions"][number];
export type MemorySearch = OperationOutput<"memory.search">;
export type RecallStatus = OperationOutput<"recall.status">;
export type RecallKbList = OperationOutput<"recall.kbList">;
export type RecallKbSpace = RecallKbList["spaces"][number];
export type RecallConnection = MemoryReview["recall"];

export const memoryKeys = {
  all: (co: string) => ["company", co, "memory"] as const,
  review: (co: string) => ["company", co, "memory", "review"] as const,
  promotions: (co: string, state: string) =>
    ["company", co, "memory", "promotions", state] as const,
  // RECALL-SEARCH: deliberately OUTSIDE `["company", co]`. A change ping (or a reconnect) that
  // invalidates the whole company would otherwise refetch an open panel's search, and each refetch
  // is a Recall search.
  search: (co: string, scope: string, q: string) => ["recall-search", co, scope, q] as const,
  status: (co: string) => ["company", co, "memory", "recall-status"] as const,
  kb: (co: string) => ["company", co, "memory", "recall-kb"] as const,
};

export const memoryReviewQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: memoryKeys.review(co),
    queryFn: () => api.client.memory.review({ params: { co } }),
  });

export type CaptureSettings = OperationOutput<"memory.captureSettings">;
export type CaptureStats = OperationOutput<"memory.captureStats">;

/** CAPTURE-LESS-NOISE: which conversations go to Recall, by source (managers). */
export const captureSettingsQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: [...memoryKeys.all(co), "capture"] as const,
    queryFn: () => api.client.memory.captureSettings({ params: { co } }),
  });

/** CAPTURE-LESS-NOISE: captures and the records Recall wrote, per day and source. */
export const captureStatsQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: [...memoryKeys.all(co), "capture-stats"] as const,
    queryFn: () => api.client.memory.captureStats({ params: { co } }),
  });

export const promotionsQuery = (api: Api, co: string, state: "accepted" | "rejected") =>
  queryOptions({
    queryKey: memoryKeys.promotions(co, state),
    queryFn: () => api.client.promotions.list({ params: { co }, query: { state, limit: 50 } }),
  });

/** How long one memory search answer is reused before a panel opened again asks Recall again. */
export const MEMORY_SEARCH_STALE_MS = 5 * 60_000;

/**
 * What Recall knows about `scope` (`company`, `task:{id}`, …), asked with `q` or its name.
 *
 * RECALL-SEARCH: one panel open is ONE search. The app's query defaults poll every 60 s and refetch
 * on focus (api/query.ts); on this query each refetch was a Recall search per space, so a panel
 * left open made the same search about once a minute (the loop Recall reported: one query ~40
 * times per space). No polling, no refetch on focus or reconnect, no retry (the API answers
 * `unavailable` rather than failing), and a 5-minute stale time; typing a new question is a new key.
 */
export const memorySearchQuery = (api: Api, co: string, scope: string, q: string) =>
  queryOptions({
    queryKey: memoryKeys.search(co, scope, q),
    queryFn: () =>
      api.client.memory.search({
        params: { co },
        query: { scope, ...(q.trim() ? { q: q.trim() } : {}) },
      }),
    staleTime: MEMORY_SEARCH_STALE_MS,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

/** RECALL-KB: the spaces' Sync to Recall switches and their last syncs (Setup › Recall). */
export const recallKbQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: memoryKeys.kb(co),
    queryFn: () => api.client.recall.kbList({ params: { co } }),
  });

/** RECALL-INBOX-LOOP: the Inbox's Memory lane. */
export type MemoryLane = OperationOutput<"memory.lane">;
export type LaneItem = MemoryLane["items"][number];
export type LaneKind = LaneItem["kind"];
export type DecisionCalibration = OperationOutput<"memory.decisionCalibration">;

export const memoryLaneQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: [...memoryKeys.all(co), "lane"] as const,
    queryFn: () => api.client.memory.lane({ params: { co } }),
  });

export const decisionCalibrationQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: [...memoryKeys.all(co), "calibration"] as const,
    queryFn: () => api.client.memory.decisionCalibration({ params: { co } }),
  });

/** RECALL-ADMIN-MEMORY: Setup › Memory (owners and admins). All under the memory prefix. */
export type AdminOverview = OperationOutput<"memory.adminOverview">;
export type AdminBrowse = OperationOutput<"memory.adminBrowse">;
export type AdminSpace = AdminBrowse["spaces"][number];
export type AdminRecord = AdminBrowse["records"][number];
export type AdminRecordPage = OperationOutput<"memory.adminRecord">;
export type AdminSpaces = OperationOutput<"memory.adminSpaces">;
export type AdminSpaceRow = AdminSpaces["spaces"][number];
export type AdminRules = OperationOutput<"memory.adminRules">;
export type AdminHealth = OperationOutput<"memory.adminHealth">;
export type AdminJob = AdminHealth["jobs"][number];

export const adminKeys = {
  all: (co: string) => [...memoryKeys.all(co), "admin"] as const,
};

export const adminOverviewQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: [...adminKeys.all(co), "overview"] as const,
    queryFn: () => api.client.memory.adminOverview({ params: { co } }),
  });

export type BrowseFilter = { space?: string; type?: string; q?: string; topic?: string };

export const adminBrowseQuery = (api: Api, co: string, f: BrowseFilter) =>
  queryOptions({
    queryKey: [...adminKeys.all(co), "browse", f] as const,
    queryFn: () =>
      api.client.memory.adminBrowse({
        params: { co },
        query: {
          ...(f.space ? { space: f.space } : {}),
          ...(f.type ? { type: f.type } : {}),
          ...(f.q ? { q: f.q } : {}),
          ...(f.topic ? { topic: f.topic } : {}),
        },
      }),
  });

/** Browse pages through a space's records: each page's `next_cursor` asks for the next. */
export const adminBrowseInfinite = (api: Api, co: string, f: BrowseFilter) =>
  infiniteQueryOptions({
    queryKey: [...adminKeys.all(co), "browse-pages", f] as const,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.client.memory.adminBrowse({
        params: { co },
        query: {
          ...(f.space ? { space: f.space } : {}),
          ...(f.type ? { type: f.type } : {}),
          ...(f.q ? { q: f.q } : {}),
          ...(f.topic ? { topic: f.topic } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      }),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

export const adminRecordQuery = (api: Api, co: string, key: string) =>
  queryOptions({
    queryKey: [...adminKeys.all(co), "record", key] as const,
    queryFn: () => api.client.memory.adminRecord({ params: { co }, query: { key } }),
  });

export const adminSpacesQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: [...adminKeys.all(co), "spaces"] as const,
    queryFn: () => api.client.memory.adminSpaces({ params: { co } }),
  });

export const adminRulesQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: [...adminKeys.all(co), "rules"] as const,
    queryFn: () => api.client.memory.adminRules({ params: { co } }),
  });

export const adminHealthQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: [...adminKeys.all(co), "health"] as const,
    queryFn: () => api.client.memory.adminHealth({ params: { co } }),
  });

export const recallStatusQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: memoryKeys.status(co),
    queryFn: () => api.client.recall.status({ params: { co } }),
  });

/** After a promotion decision: the memory screens and Work's pages (a page may be new). */
export function invalidateMemory(qc: QueryClient, co: string) {
  void qc.invalidateQueries({ queryKey: memoryKeys.all(co) });
  void qc.invalidateQueries({ queryKey: workKeys.pages(co) });
}
